import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { formatSendTime } from "@/lib/send-window";
import { edgeErrorMessage } from "@/lib/edge-errors";

export interface SendCustomerEmailInput {
  customerId: string;
  contactId: string;
  subject: string;
  text: string;
  cc?: string[];
  opportunityId?: string | null;
  followUpId?: string | null;
  inReplyToActivityId?: string | null;
}

export interface SendCustomerEmailResult {
  activity_id: string;
  status: "sent" | "scheduled";
  send_at: string;
}

/**
 * Sender e-post som den innloggede selgeren via edge function gmail-send
 * (Gmail, ikke Resend). Utenfor sendevinduet planlegges meldingen, og toasten
 * sier når den går («Planlagt sendt mandag 08:00»). Feilmeldingen kommer fra
 * edge function (`error`-feltet), ikke en generisk tekst.
 */
export function useSendCustomerEmail() {
  const qc = useQueryClient();
  return useMutation<SendCustomerEmailResult, Error, SendCustomerEmailInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke<
        SendCustomerEmailResult & { error?: string; skipped?: string }
      >("gmail-send", {
        body: {
          customer_id: input.customerId,
          contact_id: input.contactId,
          subject: input.subject,
          text: input.text,
          cc: input.cc ?? [],
          opportunity_id: input.opportunityId ?? undefined,
          follow_up_id: input.followUpId ?? undefined,
          in_reply_to_activity_id: input.inReplyToActivityId ?? undefined,
        },
      });
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke sende e-posten"));
      if (data?.skipped === "flag_off") throw new Error("E-post fra Revehiet er ikke slått på ennå");
      if (data?.error) throw new Error(data.error);
      if (!data?.activity_id) throw new Error("Uventet svar fra e-posttjenesten");
      return data;
    },
    onSuccess: async (result, input) => {
      await invalidateFor(qc, "customer_activities", { customerId: input.customerId });
      if (result.status === "scheduled") {
        toast.success(`Planlagt sendt ${formatSendTime(new Date(result.send_at))}`);
      } else {
        toast.success("Sendt");
      }
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
}
